
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from "@/components/ui/input";
import Button from '@/components/ui-elements/button';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { X, Save } from 'lucide-react';
import { Database } from '@/integrations/supabase/types';

type Reward = Database['public']['Tables']['rewards']['Row'];

interface RewardFormProps {
  editingReward: Reward | null;
  formValues: {
    name: string;
    description: string;
    points_required: number;
    quantity_available: number;
    tier_required: string;
    image_url: string;
    valid_until: string;
    is_active: boolean;
  };
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void;
  handleSubmit: (e: React.FormEvent) => Promise<void>;
  resetForm: () => void;
}

const RewardForm: React.FC<RewardFormProps> = ({ 
  editingReward, 
  formValues, 
  handleInputChange, 
  handleSubmit, 
  resetForm 
}) => {
  const { t } = useTranslation();

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {editingReward ? t('admin.editReward') : t('admin.newReward')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="block text-sm font-medium">{t('admin.rewardName')}</label>
              <Input
                name="name"
                value={formValues.name}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium">{t('admin.pointsRequired')}</label>
              <Input
                type="number"
                name="points_required"
                value={formValues.points_required}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium">{t('admin.quantity')}</label>
              <Input
                type="number"
                name="quantity_available"
                value={formValues.quantity_available}
                onChange={handleInputChange}
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium">{t('admin.tierRequired')}</label>
              <select
                name="tier_required"
                value={formValues.tier_required}
                onChange={handleInputChange}
                className="w-full border rounded-md p-2"
              >
                <option value="standard">Standard</option>
                <option value="silver">Silver</option>
                <option value="gold">Gold</option>
                <option value="platinum">Platinum</option>
              </select>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium">{t('admin.imageUrl')}</label>
              <Input
                name="image_url"
                value={formValues.image_url}
                onChange={handleInputChange}
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium">{t('admin.validUntil')}</label>
              <Input
                type="date"
                name="valid_until"
                value={formValues.valid_until}
                onChange={handleInputChange}
              />
            </div>

            <div className="space-y-2 md:col-span-2">
              <label className="block text-sm font-medium">{t('admin.description')}</label>
              <textarea
                name="description"
                value={formValues.description}
                onChange={handleInputChange}
                className="w-full border rounded-md p-2 min-h-[100px]"
              ></textarea>
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                name="is_active"
                checked={formValues.is_active}
                onChange={(e) => handleInputChange({
                  ...e,
                  target: {
                    ...e.target,
                    name: 'is_active',
                    value: e.target.checked.toString()
                  }
                } as React.ChangeEvent<HTMLInputElement>)}
                id="is_active"
              />
              <label htmlFor="is_active" className="text-sm font-medium">{t('admin.isActive')}</label>
            </div>
          </div>

          <div className="flex justify-end space-x-2">
            <Button
              type="button"
              variant="outline"
              onClick={resetForm}
              leftIcon={<X className="h-4 w-4" />}
            >
              {t('admin.cancel')}
            </Button>
            <Button
              type="submit"
              leftIcon={<Save className="h-4 w-4" />}
            >
              {t('admin.saveReward')}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

export default RewardForm;
